import React from 'react';
import Dialog from './Dialog';
import { IconWarn } from './dialogIcons';
import { fieldWords, planIsEmpty, planSentence } from '../lib/importPlan';

/*
  What a file is about to do, before it does it.

  Written 6 September 2026 for "OnHand's own templates, and an import that says
  where it is going".

  **Every import in OnHand was a one-way trip you could not see the end of.** You
  chose a file, pressed a button, and rows went in. What came back afterwards was
  a number that counted the rows written and made no distinction between a new
  location and one that already existed and was changed.

  **And none of them ever said which company they were loading into**, which is
  the one this exists for. A row's company comes from whichever one you are
  signed into. That is the right behaviour. Having no confirmation is not: once
  the real business is loaded, one import while pointed at the testing site puts
  tens of thousands of rows in the wrong place and nothing anywhere would say so
  until somebody noticed.

  **The company's name is the first thing on it and it is in the title**, not
  tucked into a sentence underneath, because it is the one thing a person has to
  read before pressing anything.
*/
export default function ImportPreview({ def, plan, company, fileName, busy, onClose, onConfirm }) {
  const nothing = planIsEmpty(plan);
  const total = plan.create.length + plan.update.length;

  return (
    <Dialog
      kind={nothing ? 'info' : 'question'}
      icon={<IconWarn />}
      title={'Load into ' + (company || 'this company') + '?'}
      record={fileName}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-quiet" onClick={onClose} disabled={busy}>Cancel</button>
          {/* Rule 33: a locked act is a state of the act, not a missing one. A
              file that would change nothing gets a grey button rather than no
              button, so it is clear that loading is the thing being refused. */}
          <button className="btn btn-primary" onClick={onConfirm} disabled={busy || nothing}>
            {busy ? 'Loading...' : nothing ? 'Nothing to load' : 'Load ' + total.toLocaleString() + ' ' + noun(def, total)}
          </button>
        </>
      }
    >
      <div className="imp-says">{planSentence(plan, def.noun)}</div>

      <div className="rpsum" style={{ marginBottom: 14 }}>
        <Figure n={plan.create.length} label="New" />
        <Figure n={plan.update.length} label="Changed" />
        <Figure n={plan.unchanged.length} label="Already the same" />
        <Figure n={plan.unreadable.length} label="Cannot be read"
          red={plan.unreadable.length > 0} />
      </div>

      {plan.unreadable.length > 0 && (
        <>
          <div className="imp-head">The rows it cannot read, and why</div>
          <div className="imp-bad">
            {plan.unreadable.slice(0, 12).map((b, i) => (
              <div key={i} className="imp-bad-row">
                <span className="imp-line">Line {b.line}</span>
                <span>{b.why}</span>
              </div>
            ))}
            {plan.unreadable.length > 12 && (
              <div className="imp-bad-row" style={{ color: 'var(--text-secondary)' }}>
                and {plan.unreadable.length - 12} more
              </div>
            )}
          </div>
          {/* Skipping them is a decision, so it is said out loud rather than
              left to be discovered by a row count that does not add up. */}
          <div className="imp-note">
            Loading now writes everything above and leaves these alone. Nothing is
            half written: a row either goes in whole or is listed here.
          </div>
        </>
      )}

      {plan.update.length > 0 && (
        <>
          <div className="imp-head">The first few it would change</div>
          <div className="imp-bad">
            {plan.update.slice(0, 6).map((u, i) => (
              <div key={i} className="imp-bad-row">
                <span className="imp-line">Line {u.line}</span>
                <span>{u.changed.map(fieldWords).join(', ')}</span>
              </div>
            ))}
            {plan.update.length > 6 && (
              <div className="imp-bad-row" style={{ color: 'var(--text-secondary)' }}>
                and {plan.update.length - 6} more
              </div>
            )}
          </div>
        </>
      )}
    </Dialog>
  );
}

function noun(def, n) {
  const word = def.noun || 'row';
  return n === 1 ? word : word + 's';
}

function Figure({ n, label, red }) {
  return (
    <span className="rpsum-c">
      <b className={'rpsum-n' + (red ? ' red' : '')}>{n.toLocaleString()}</b>
      <span className="rpsum-l">{label}</span>
    </span>
  );
}
